/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type CSSProperties,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { keyframes } from '@compiled/react';
import { type IntlShape, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import type MediaAboveSm from '@atlaskit/css/at-rules/media-above-sm';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import DecisionIcon from '@atlaskit/icon/core/decision';
import EpicIcon from '@atlaskit/icon/core/epic';
import PageIcon from '@atlaskit/icon/core/page';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import ProjectIcon from '@atlaskit/icon/core/project';
import VideoIcon from '@atlaskit/icon/core/video';
import Image from '@atlaskit/image/image';
import IntlMessagesProvider from '@atlaskit/intl-messages-provider/main';
import { ConfluenceIcon } from '@atlaskit/logo/confluence/icon';
import { JiraIcon } from '@atlaskit/logo/jira/icon';
import { LoomIcon } from '@atlaskit/logo/loom/icon';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import Lozenge from '@atlaskit/lozenge/new/lozenge';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { ClaudeIcon } from '@atlassian/logo-third-party/claude';
import { FigmaIcon } from '@atlassian/logo-third-party/figma';
import { GitlabIcon } from '@atlassian/logo-third-party/gitlab';
import { MiroIcon } from '@atlassian/logo-third-party/miro';

import collectionVectorDarkSrc from './assets/collection-vector-darkmode.svg';
import collectionVectorSrc from './assets/collection-vector.svg';
import novaAvatarSrc from './assets/nova-color.png';
import teamworkMarkTiltSrc from './assets/teamwork-mark-tilt.svg';
import {
	type AvatarId,
	edges,
	type GraphEdge,
	type GraphNode,
	type NewProductId,
	nodes,
	type ProductId,
	type ThirdPartyAppId,
	type WorkGlyph,
} from './graph-data';
import defaultMessages from './i18n/en';
import { fetchMessagesForLocale } from './i18n/fetch-messages-for-locale';
import { messages } from './messages';
import { playEncodedAudio } from './play-encoded-audio';

const NEW_PRODUCT_ORDER = [
	'jira',
	'confluence',
	'loom',
	'rovo',
] as const satisfies readonly NewProductId[];
const DEFAULT_NEW_PRODUCTS = ['loom', 'rovo'] as const satisfies readonly NewProductId[];
const STAGE_AT_BY_NEW_PRODUCT_COUNT = [
	[0, 800, 5400, 6800],
	[0, 800, 1800, 5400, 6800],
	[0, 800, 1800, 3200, 5400, 6800],
	[0, 800, 1800, 3000, 4200, 5400, 6800],
	[0, 800, 1800, 2700, 3600, 4500, 5400, 6800],
] as const;
const FINAL_HOLD_MS = 800;
const REDUCED_MOTION_RUN_MS = 1250;
const REDUCED_MOTION_HOLD_MS = 550;
const FADE_MS = 400;
const CHIP_HEIGHT = 32;
const AUDIO_VOLUME = 0.25;
const TEST_AUDIO_URL = 'test-file-stub';

const defaultAvatarSources: Partial<Record<AvatarId, string>> = {
	you: novaAvatarSrc,
};

const nodePop = keyframes({
	from: { opacity: 0, transform: 'scale(0.4)' },
	'65%': { opacity: 1, transform: 'scale(1.06)' },
	to: { opacity: 1, transform: 'scale(1)' },
});

const nodeDrift = keyframes({
	from: {
		opacity: 0,
		transform: 'translate(var(--node-dx), var(--node-dy)) scale(0.3)',
	},
	'25%': { opacity: 1 },
	to: { opacity: 1, transform: 'translate(0, 0) scale(1)' },
});

const nodeDriftSoft = keyframes({
	from: {
		opacity: 0,
		transform: 'translate(var(--node-dx), var(--node-dy)) scale(0.62)',
	},
	'45%': { opacity: 1 },
	to: { opacity: 1, transform: 'translate(0, 0) scale(1)' },
});

const edgeDraw = keyframes({
	from: { strokeDashoffset: 1 },
	to: { strokeDashoffset: 0 },
});

const pulseRing = keyframes({
	from: { opacity: 0.55, transform: 'scale(1)' },
	to: { opacity: 0, transform: 'scale(2.1)' },
});

const calloutIn = keyframes({
	from: { opacity: 0, transform: 'translateY(5px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const edgePulse = keyframes({
	from: { strokeOpacity: 0.85, strokeWidth: 2 },
	to: { strokeOpacity: 0, strokeWidth: 8 },
});

const subheadingIn = keyframes({
	from: { opacity: 0, transform: 'translateY(6px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const styles = cssMap({
	root: {
		position: 'relative',
		width: '100%',
		minHeight: '868px',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		boxShadow: token('elevation.shadow.raised'),
	},
	rootModal: {
		boxSizing: 'border-box',
		height: '568px',
		minHeight: '568px',
	},
	rootFullscreen: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		height: 'auto',
		minHeight: 0,
		minWidth: 0,
		overflowX: 'hidden',
		overflowY: 'auto',
		backgroundColor: 'transparent',
		borderRadius: 0,
		borderWidth: 0,
		boxShadow: 'initial',
	},
	content: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.600'),
		paddingTop: token('space.1000'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.1000'),
		paddingLeft: token('space.200'),
		transitionProperty: 'opacity, transform',
		transitionDuration: `${FADE_MS}ms`,
		transitionTimingFunction: 'ease-in',
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0ms',
		},
		'@media (min-width: 48rem)': {
			paddingRight: token('space.600'),
			paddingLeft: token('space.600'),
		},
	},
	contentFading: {
		opacity: 0,
		transform: 'translateY(-10px)',
	},
	contentModal: {
		height: '100%',
		boxSizing: 'border-box',
		justifyContent: 'center',
		gap: token('space.200'),
		paddingTop: token('space.300'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.400'),
		'@media (min-width: 48rem)': {
			paddingRight: token('space.400'),
			paddingLeft: token('space.400'),
		},
	},
	contentFullscreen: {
		boxSizing: 'border-box',
		flexShrink: 0,
		width: '100%',
		minHeight: 0,
		gap: token('space.400'),
		paddingTop: token('space.0'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		['@media (min-width: 48rem)' satisfies MediaAboveSm]: {
			paddingLeft: token('space.0'),
			paddingRight: token('space.0'),
		},
	},
	controls: {
		position: 'absolute',
		top: token('space.400'),
		right: token('space.400'),
		zIndex: 1,
	},
	header: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.150'),
		maxWidth: '720px',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		textAlign: 'center',
	},
	headerModal: {
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
	},
	headerFullscreen: {
		flexShrink: 0,
		maxWidth: '854px',
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
	},
	collectionMark: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		height: '39px',
	},
	teamworkMark: {
		display: 'block',
		width: '92px',
		height: '39px',
	},
	subheading: {
		minHeight: '24px',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		color: token('color.text.subtle'),
		font: token('font.body.large'),
		animationName: subheadingIn,
		animationDuration: '350ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'both',
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: '0ms',
		},
	},
	graph: {
		width: '100%',
		maxWidth: '1310px',
	},
	graphFullscreen: {
		maxWidth: '999px',
	},
	svg: {
		display: 'block',
		width: '100%',
		height: 'auto',
		overflow: 'visible',
	},
	edge: {
		animationName: edgeDraw,
		animationDuration: '400ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'both',
		animationDelay: 'var(--edge-delay)',
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: '0ms',
			animationDelay: '0ms',
		},
	},
	edgePulse: {
		animationName: edgePulse,
		animationDuration: '1200ms',
		animationTimingFunction: 'ease-out',
		animationDelay: '100ms',
		animationIterationCount: 1,
		animationFillMode: 'both',
		'@media (prefers-reduced-motion: reduce)': {
			display: 'none',
		},
	},
	node: {
		animationName: nodePop,
		animationDuration: '350ms',
		animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
		animationFillMode: 'both',
		animationDelay: 'var(--node-delay)',
		transformOrigin: 'center',
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: '0ms',
			animationDelay: '0ms',
		},
	},
	nodeDrift: {
		animationName: nodeDrift,
		animationDuration: '550ms',
		animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
	},
	nodeDriftSoft: {
		animationName: nodeDriftSoft,
		animationDuration: '800ms',
		animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
	},
	chip: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		height: `${CHIP_HEIGHT}px`,
		paddingTop: token('space.0'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.100'),
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.brand'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.raised'),
		color: token('color.text'),
		font: token('font.body.small'),
		fontFamily: token('font.family.code'),
		whiteSpace: 'nowrap',
	},
	/**
	 * Invisible but laid out (not `display: none`) so `getComputedStyle` resolves a real, literal
	 * font string from the same tokens `chip` uses — see `chipWidth`'s use of `cachedChipFont`.
	 */
	chipFontProbe: {
		position: 'absolute',
		top: 0,
		left: 0,
		height: 0,
		overflow: 'hidden',
		visibility: 'hidden',
		pointerEvents: 'none',
		font: token('font.body.small'),
		fontFamily: token('font.family.code'),
	},
	chipNonTc: {
		borderColor: token('color.border'),
	},
	glyph: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '16px',
		height: '16px',
	},
	productGlyphTile: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		borderRadius: token('radius.xsmall'),
	},
	personLabel: {
		font: token('font.body.small'),
		fill: token('color.text'),
	},
	callout: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.brand'),
		borderRadius: token('radius.medium'),
		boxShadow: token('elevation.shadow.overlay'),
		color: token('color.text'),
		font: token('font.body.small'),
		animationName: calloutIn,
		animationDuration: '500ms',
		animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
		animationDelay: '150ms',
		animationFillMode: 'both',
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: '0ms',
		},
	},
	pulse: {
		animationName: pulseRing,
		animationDuration: '900ms',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'both',
		transformBox: 'fill-box',
		transformOrigin: 'center',
		'@media (prefers-reduced-motion: reduce)': {
			display: 'none',
		},
	},
	youPulse: {
		animationName: pulseRing,
		animationDuration: '1800ms',
		animationTimingFunction: 'ease-out',
		animationDelay: '600ms',
		animationIterationCount: 1,
		animationFillMode: 'both',
		transformBox: 'fill-box',
		transformOrigin: 'center',
		'@media (prefers-reduced-motion: reduce)': {
			display: 'none',
		},
	},
});

type AnimationVariables = CSSProperties & {
	'--edge-delay'?: string;
	'--node-delay'?: string;
	'--node-dx'?: string;
	'--node-dy'?: string;
};

type AnimationGraph = {
	appsStage: number;
	edges: GraphEdge[];
	newProducts: NewProductId[];
	nodeById: Map<string, GraphNode>;
	nodeDelay: Map<string, number>;
	nodes: GraphNode[];
	parentOffsets: Map<string, { dx: number; dy: number }>;
	stageAt: readonly number[];
	webStage: number;
};

const getNewProductsKey = (newProducts: readonly NewProductId[]): string => {
	const selectedProducts = new Set(newProducts);
	return NEW_PRODUCT_ORDER.filter((product) => selectedProducts.has(product)).join('|');
};

const buildAnimationGraph = (newProductsKey: string): AnimationGraph => {
	const selectedProducts = new Set(
		NEW_PRODUCT_ORDER.filter((product) => newProductsKey.split('|').includes(product)),
	);
	const newProducts = NEW_PRODUCT_ORDER.filter((product) => selectedProducts.has(product));
	const appsStage = 1;
	const webStage = newProducts.length + 2;
	const newProductStages = new Map(
		newProducts.map((product, index) => [product, index + 2] as const),
	);
	const graphNodes = nodes.map((node): GraphNode => {
		if (node.kind === 'person') {
			return node;
		}
		if (node.kind === 'product') {
			const newProductStage = newProductStages.get(node.product);
			return {
				...node,
				stage: newProductStage ?? appsStage,
				isNew: newProductStage !== undefined || undefined,
			};
		}
		return { ...node, stage: webStage };
	});
	const nodeById = new Map(graphNodes.map((node) => [node.id, node]));
	const graphEdges = edges.map((edge): GraphEdge => {
		const targetNode = edge.from === 'you' ? nodeById.get(edge.to) : undefined;
		return {
			...edge,
			...(edge.from === 'you' && edge.to === 'jira' && selectedProducts.has('jira')
				? { bend: 100 }
				: {}),
			stage: targetNode?.kind === 'product' ? targetNode.stage : webStage,
		};
	});
	const counts = new Map<number, number>();
	const nodeDelay = new Map<string, number>();
	for (const node of graphNodes) {
		const index = counts.get(node.stage) ?? 0;
		counts.set(node.stage, index + 1);
		nodeDelay.set(node.id, node.stage === webStage ? index * 0.06 + 0.12 : index * 0.06);
	}
	for (const node of graphNodes) {
		if (node.kind === 'product' && node.stage === appsStage) {
			nodeDelay.set(node.id, 0);
		}
	}
	const parentOffsets = new Map<string, { dx: number; dy: number }>();
	for (const node of graphNodes) {
		const parentEdge = graphEdges.find((edge) => edge.to === node.id && edge.stage === node.stage);
		const parent = parentEdge ? nodeById.get(parentEdge.from) : undefined;
		if (parent) {
			parentOffsets.set(node.id, { dx: parent.x - node.x, dy: parent.y - node.y });
		}
	}
	return {
		appsStage,
		edges: graphEdges,
		newProducts,
		nodeById,
		nodeDelay,
		nodes: graphNodes,
		parentOffsets,
		stageAt: STAGE_AT_BY_NEW_PRODUCT_COUNT[newProducts.length],
		webStage,
	};
};

const curvePath = (from: GraphNode, to: GraphNode, bend = 0): string => {
	const middleX = (from.x + to.x) / 2;
	const middleY = (from.y + to.y) / 2;
	const deltaX = to.x - from.x;
	const deltaY = to.y - from.y;
	const length = Math.hypot(deltaX, deltaY) || 1;
	const controlX = middleX + (-deltaY / length) * bend;
	const controlY = middleY + (deltaX / length) * bend;
	return `M ${from.x} ${from.y} Q ${controlX} ${controlY} ${to.x} ${to.y}`;
};

/**
 * The chip's real font, once resolved from the hidden `chipFontProbe` element (see the
 * `useLayoutEffect` in `IntroAnimation`). Cached at module scope, outside React state, so every
 * instance of this animation after the first reuses the measurement instead of re-probing — the
 * `chipFont` state in `IntroAnimation` is just this value made visible to a single render.
 */
let cachedChipFont: string | undefined;
let measurementCanvasContext: CanvasRenderingContext2D | undefined | null;

/**
 * The real rendered width of the "New" `Lozenge`, once measured from the hidden
 * `newLozengeProbeRef` element (see the `useLayoutEffect` in `IntroAnimation`). Cached at module
 * scope for the same reason as `cachedChipFont`. Measuring rather than guessing matters here
 * because the "New" label is translated, so its rendered width varies by locale.
 */
let cachedNewLozengeWidth: number | undefined;

/**
 * Average per-character width used before the real chip font has been measured (first paint of the
 * first instance only) or when `canvas`/`document` is unavailable (SSR). Matches this component's
 * previous fixed estimate so early layout doesn't regress while the real measurement lands.
 */
const FALLBACK_CHAR_WIDTH = 7.4;

/** Gap between the label and the "New" `Lozenge`, matching the chip's `gap: token('space.075')`. */
const NEW_LOZENGE_GAP = 6;

/**
 * Reserved width for the gap + "New" `Lozenge` before it has been measured. Matches this
 * component's previous fixed estimate so early layout doesn't regress while the real measurement
 * lands.
 */
const FALLBACK_NEW_LOZENGE_SLOT = 44;

/**
 * Creates the canvas used to measure text (if it hasn't been already) and points it at `font`.
 * Called from the `useLayoutEffect` that resolves the chip font (see `IntroAnimation`), never
 * during render — `document.createElement` and mutating the canvas's `font` are side effects that
 * must not run while rendering.
 */
const prepareMeasurementCanvasContext = (font: string): void => {
	// `undefined` means "not checked yet", `null` means "checked, canvas 2D isn't available".
	if (measurementCanvasContext === undefined) {
		measurementCanvasContext = getDocument()?.createElement('canvas').getContext('2d') ?? null;
	}
	if (measurementCanvasContext) {
		measurementCanvasContext.font = font;
	}
};

const measureLabelWidth = (label: string, font: string | undefined): number => {
	if (!font || !measurementCanvasContext) {
		return label.length * FALLBACK_CHAR_WIDTH;
	}

	return measurementCanvasContext.measureText(label).width;
};

const chipWidth = (
	label: string,
	font: string | undefined,
	newLozengeWidth: number | undefined,
	isNew = false,
): number =>
	Math.max(
		76,
		42 +
			measureLabelWidth(label, font) +
			(isNew
				? newLozengeWidth !== undefined
					? NEW_LOZENGE_GAP + newLozengeWidth
					: FALLBACK_NEW_LOZENGE_SLOT
				: 0),
	);

const ProductGlyph = ({ product }: { product: ProductId }) => {
	switch (product) {
		case 'confluence':
			return (
				<span css={styles.productGlyphTile}>
					<ConfluenceIcon label="" size="xxsmall" />
				</span>
			);
		case 'jira':
			return (
				<span css={styles.productGlyphTile}>
					<JiraIcon label="" size="xxsmall" />
				</span>
			);
		case 'loom':
			return (
				<span css={styles.productGlyphTile}>
					<LoomIcon label="" size="xxsmall" />
				</span>
			);
		case 'rovo':
			return <RovoIcon appearance="brand" label="" size="xxsmall" />;
	}
};

const ThirdPartyGlyph = ({ app, testId }: { app: ThirdPartyAppId; testId: string }) => {
	switch (app) {
		case 'claude':
			return <ClaudeIcon label="" size="small" testId={testId} />;
		case 'figma':
			return <FigmaIcon label="" size="small" testId={testId} />;
		case 'gitlab':
			return <GitlabIcon label="" size="small" testId={testId} />;
		case 'miro':
			return <MiroIcon label="" size="small" testId={testId} />;
	}
};

const WorkIcon = ({ glyph }: { glyph: WorkGlyph }) => {
	switch (glyph) {
		case 'decision':
			return <DecisionIcon label="" size="small" />;
		case 'epic':
			return <EpicIcon label="" size="small" />;
		case 'page':
			return <PageIcon label="" size="small" />;
		case 'project':
			return <ProjectIcon label="" size="small" />;
		case 'team':
			return <PeopleGroupIcon label="" size="small" />;
		case 'video':
			return <VideoIcon label="" size="small" />;
	}
};

const getNodeLabel = (
	node: Extract<GraphNode, { kind: 'product' | 'work' }>,
	formatMessage: IntlShape['formatMessage'],
): string => {
	if (node.kind === 'product') {
		switch (node.product) {
			case 'confluence':
				return formatMessage(messages.confluence);
			case 'jira':
				return formatMessage(messages.jira);
			case 'loom':
				return formatMessage(messages.loom);
			case 'rovo':
				return formatMessage(messages.rovo);
		}
	}
	switch (node.glyph) {
		case 'decision':
			return formatMessage(messages.decision);
		case 'epic':
			return formatMessage(messages.epic);
		case 'page':
			return formatMessage(messages.page);
		case 'project':
			return formatMessage(messages.project);
		case 'team':
			return formatMessage(messages.team);
		case 'video':
			return formatMessage(messages.meetingRecording);
	}
};

const getInitials = (userName?: string): string => {
	if (!userName?.trim()) {
		return 'YO';
	}
	return userName
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.join('');
};

const productCallouts = {
	// ADS body.small is 12px; 216 keeps the Jira copy on one line with the same side spacing
	// as the 200-unit reference card's 11px text.
	jira: { message: messages.jiraCallout, width: 216 },
	confluence: { message: messages.confluenceCallout, width: 168 },
	loom: { message: messages.loomCallout, width: 158 },
	rovo: { message: messages.rovoCallout, width: 148 },
} satisfies Record<
	NewProductId,
	{ message: (typeof messages)[keyof typeof messages]; width: number }
>;

const getEdgeDelay = (
	edge: GraphEdge,
	nodeById: Map<string, GraphNode>,
	nodeDelay: Map<string, number>,
	appsStage: number,
): number => {
	const endpoints = [nodeById.get(edge.from), nodeById.get(edge.to)].filter(
		(node): node is GraphNode => Boolean(node),
	);
	return (
		Math.max(
			...endpoints
				.filter((node) => node.stage === edge.stage)
				.map((node) => nodeDelay.get(node.id) ?? 0),
			0,
		) + (edge.stage === appsStage ? 0.3 : 0.1)
	);
};

const prefersReducedMotion = (): boolean =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export type TwcgOnboardingAvatarId = AvatarId;
export type TwcgOnboardingNewProduct = NewProductId;

export type TwcgOnboardingIntroAnimationProps = {
	/** Optional image URLs that override the bundled portraits in the fixed graph composition. */
	avatarSources?: Partial<Record<TwcgOnboardingAvatarId, string>>;
	/** Holds the final frame until the host is ready to show the next screen. Defaults to true. */
	canComplete?: boolean;
	/** Shows only the welcome header until data is ready for playback. Defaults to true. */
	canStart?: boolean;
	/** Heading level used when the animation is placed in its host modal. */
	headingLevel?: 'h1' | 'h2';
	/** Hides the internal Skip control when the host already provides a dismiss control. */
	hideSkip?: boolean;
	/** Standalone card, fixed-height XFlow card, or borderless content beneath the host's logo. */
	layout?: 'default' | 'modal' | 'fullscreen';
	/** Products introduced as new, each with its own animation beat and benefit callout. */
	newProducts?: readonly TwcgOnboardingNewProduct[];
	/** Called after playback finishes or is skipped, once canComplete is true and the fade ends. */
	onComplete?: () => void;
	/** Called once when playback starts, after canStart becomes true. */
	onShown?: () => void;
	/** Called when the user skips the animation, before completion. */
	onSkip?: () => void;
	/** Stable test identifier applied to the package root. */
	testId?: string;
	/** Optional display name used in the welcome heading and central avatar. */
	userName?: string;
};

const IntroAnimation = ({
	avatarSources,
	canComplete = true,
	canStart = true,
	headingLevel = 'h1',
	hideSkip = false,
	layout = 'default',
	newProducts = DEFAULT_NEW_PRODUCTS,
	onComplete,
	onShown,
	onSkip,
	testId = 'twcg-onboarding-intro-animation',
	userName,
}: TwcgOnboardingIntroAnimationProps) => {
	const { formatMessage } = useIntl();
	const avatarClipPrefix = useId().replace(/:/g, '');
	const reducedMotion = useMemo(prefersReducedMotion, []);
	const newProductsKey = getNewProductsKey(newProducts);
	const animationGraph = useMemo(() => buildAnimationGraph(newProductsKey), [newProductsKey]);
	const {
		appsStage,
		edges: graphEdges,
		newProducts: normalizedNewProducts,
		nodeById,
		nodeDelay,
		nodes: graphNodes,
		parentOffsets,
		stageAt,
		webStage,
	} = animationGraph;
	const [stage, setStage] = useState(0);
	const [isFading, setIsFading] = useState(false);
	const [hasYouPulseFinished, setHasYouPulseFinished] = useState(false);
	const stopAudioRef = useRef<(() => void) | null>(null);
	// Timeline callbacks read current readiness without restarting playback when the host updates it.
	const hasPlaybackFinishedRef = useRef(false);
	const canCompleteRef = useRef(canComplete);
	const hasCompletedRef = useRef(false);
	const hasShownRef = useRef(false);
	const finishTimerRef = useRef<number | undefined>(undefined);
	const onCompleteRef = useRef(onComplete);
	const onShownRef = useRef(onShown);
	const chipFontProbeRef = useRef<HTMLSpanElement | null>(null);
	const newLozengeProbeRef = useRef<HTMLSpanElement | null>(null);
	const [chipFont, setChipFont] = useState(cachedChipFont);
	const [newLozengeWidth, setNewLozengeWidth] = useState(cachedNewLozengeWidth);
	const stopAudio = useCallback(() => {
		const stop = stopAudioRef.current;
		stopAudioRef.current = null;
		stop?.();
	}, []);

	useLayoutEffect(() => {
		if (chipFont || !chipFontProbeRef.current) {
			return;
		}
		const font = getComputedStyle(chipFontProbeRef.current).font;
		if (font) {
			prepareMeasurementCanvasContext(font);
			cachedChipFont = font;
			setChipFont(font);
		}
	}, [chipFont]);

	useLayoutEffect(() => {
		if (newLozengeWidth !== undefined || !newLozengeProbeRef.current) {
			return;
		}
		const width = newLozengeProbeRef.current.getBoundingClientRect().width;
		if (width) {
			cachedNewLozengeWidth = width;
			setNewLozengeWidth(width);
		}
	}, [newLozengeWidth]);

	useEffect(() => {
		if (!canStart || hasShownRef.current) {
			return;
		}
		hasShownRef.current = true;
		onShownRef.current?.();
	}, [canStart]);

	useEffect(() => {
		onCompleteRef.current = onComplete;
	}, [onComplete]);

	const finish = useCallback(() => {
		if (hasCompletedRef.current) {
			return;
		}
		hasCompletedRef.current = true;
		setIsFading(true);
		finishTimerRef.current = window.setTimeout(() => {
			stopAudio();
			onCompleteRef.current?.();
		}, FADE_MS);
	}, [stopAudio]);

	useEffect(() => {
		canCompleteRef.current = canComplete;
		if (hasPlaybackFinishedRef.current && canComplete) {
			finish();
		}
	}, [canComplete, finish]);

	const finishPlayback = useCallback(() => {
		if (hasPlaybackFinishedRef.current) {
			return;
		}
		hasPlaybackFinishedRef.current = true;
		if (canCompleteRef.current) {
			finish();
		} else {
			stopAudio();
		}
	}, [finish, stopAudio]);

	const skip = useCallback(() => {
		if (hasPlaybackFinishedRef.current) {
			return;
		}
		onSkip?.();
		stopAudio();
		if (!canCompleteRef.current) {
			setStage(stageAt.length - 1);
		}
		finishPlayback();
	}, [finishPlayback, onSkip, stageAt.length, stopAudio]);

	useEffect(() => {
		if (!canStart) {
			return;
		}
		let isMounted = true;
		if (process.env.NODE_ENV === 'test') {
			const audio = new Audio(TEST_AUDIO_URL);
			audio.volume = AUDIO_VOLUME;
			stopAudioRef.current = () => audio.pause();
			void audio.play().catch(() => undefined);
		} else {
			void import(
				/* webpackChunkName: "@atlassian/twcg-onboarding-sonic-logo" */
				'./assets/sonic-logo-synths'
			).then(
				({ sonicLogoSynthsBase64 }) => {
					void playEncodedAudio(sonicLogoSynthsBase64, AUDIO_VOLUME).then((stopAudio) => {
						if (!isMounted || hasPlaybackFinishedRef.current) {
							stopAudio();
							return;
						}
						stopAudioRef.current = stopAudio;
					});
				},
				() => undefined,
			);
		}
		return () => {
			isMounted = false;
			stopAudio();
		};
	}, [canStart, stopAudio]);

	useEffect(() => {
		if (!canStart) {
			return;
		}
		const timers: number[] = [];
		const timeline = reducedMotion
			? stageAt.map((_, index) =>
					Math.round((index * REDUCED_MOTION_RUN_MS) / (stageAt.length - 1)),
				)
			: stageAt;
		for (let nextStage = 1; nextStage < timeline.length; nextStage++) {
			timers.push(
				window.setTimeout(() => {
					if (!hasPlaybackFinishedRef.current) {
						setStage(nextStage);
					}
				}, timeline[nextStage]),
			);
		}
		const holdMs = reducedMotion ? REDUCED_MOTION_HOLD_MS : FINAL_HOLD_MS;
		const lastStageAt = timeline[timeline.length - 1] ?? 0;
		timers.push(window.setTimeout(finishPlayback, lastStageAt + holdMs));
		return () => {
			timers.forEach(window.clearTimeout);
			if (finishTimerRef.current !== undefined) {
				window.clearTimeout(finishTimerRef.current);
			}
		};
	}, [canStart, finishPlayback, reducedMotion, stageAt]);

	const statusMessage = stage >= webStage && canComplete ? messages.ready : messages.finding;
	const heading = userName
		? formatMessage(messages.welcomeWithName, { userName })
		: formatMessage(messages.welcome);
	const userInitials = getInitials(userName);
	const isModalLayout = layout === 'modal';
	const isFullscreenLayout = layout === 'fullscreen';

	return (
		<section
			css={[
				styles.root,
				isModalLayout && styles.rootModal,
				isFullscreenLayout && styles.rootFullscreen,
			]}
			data-testid={testId}
			aria-label={heading}
		>
			<span
				ref={chipFontProbeRef}
				css={styles.chipFontProbe}
				aria-hidden="true"
				data-testid={`${testId}-chip-font-probe`}
			/>
			<span
				ref={newLozengeProbeRef}
				css={styles.chipFontProbe}
				aria-hidden="true"
				data-testid={`${testId}-new-lozenge-probe`}
			>
				<Lozenge appearance="discovery">{formatMessage(messages.new)}</Lozenge>
			</span>
			{canStart && !hideSkip && (
				<div css={styles.controls}>
					<IconButton
						appearance="default"
						icon={CrossIcon}
						label={formatMessage(messages.skip)}
						onClick={skip}
						shape="circle"
						testId={`${testId}-skip`}
					/>
				</div>
			)}
			<div
				css={[
					styles.content,
					isModalLayout && styles.contentModal,
					isFullscreenLayout && styles.contentFullscreen,
					isFading && styles.contentFading,
				]}
			>
				<header
					css={[
						styles.header,
						isModalLayout && styles.headerModal,
						isFullscreenLayout && styles.headerFullscreen,
					]}
				>
					{!isFullscreenLayout && (
						<div css={styles.collectionMark} aria-hidden="true">
							<img css={styles.teamworkMark} src={teamworkMarkTiltSrc} alt="" />
							<Image
								src={collectionVectorSrc}
								srcDark={collectionVectorDarkSrc}
								alt=""
								width="67"
								height="12"
							/>
						</div>
					)}
					<Heading as={headingLevel} size={isFullscreenLayout ? 'xxlarge' : 'xlarge'}>
						{heading}
					</Heading>
					<div key={statusMessage.id} css={styles.subheading}>
						<Text color="color.text.subtle" size="large">
							{formatMessage(statusMessage)}
						</Text>
					</div>
				</header>

				<div
					css={[styles.graph, isFullscreenLayout && styles.graphFullscreen]}
					aria-hidden={!canStart}
				>
					<svg
						css={styles.svg}
						viewBox="0 0 1408 520"
						role="img"
						aria-label={formatMessage(messages.graphLabel)}
					>
						{canStart && (
							<g aria-hidden="true">
								{graphEdges.map((edge) => {
									if (stage < edge.stage) {
										return null;
									}
									const from = nodeById.get(edge.from);
									const to = nodeById.get(edge.to);
									if (!from || !to) {
										return null;
									}
									return (
										<path
											key={`${edge.from}-${edge.to}`}
											css={styles.edge}
											d={curvePath(from, to, edge.bend)}
											fill="none"
											stroke={token('color.border.bold')}
											strokeWidth={1.8}
											strokeOpacity={0.72}
											strokeLinecap="round"
											pathLength={1}
											strokeDasharray={1}
											style={
												{
													'--edge-delay': `${getEdgeDelay(
														edge,
														nodeById,
														nodeDelay,
														appsStage,
													).toFixed(2)}s`,
												} as AnimationVariables
											}
										/>
									);
								})}

								{stage === webStage &&
									graphEdges
										.filter((edge) => edge.stage < webStage)
										.map((edge) => {
											const from = nodeById.get(edge.from);
											const to = nodeById.get(edge.to);
											if (!from || !to) {
												return null;
											}
											return (
												<path
													key={`pulse-${edge.from}-${edge.to}`}
													css={styles.edgePulse}
													d={curvePath(from, to, edge.bend)}
													fill="none"
													stroke={token('color.border.brand')}
													strokeLinecap="round"
												/>
											);
										})}

								{graphNodes.map((node) => {
									if (stage < node.stage) {
										return null;
									}
									const offset = parentOffsets.get(node.id);
									if (node.kind === 'person') {
										const initials = node.id === 'you' ? userInitials : node.initials;
										const avatarSource =
											avatarSources?.[node.avatarId] ?? defaultAvatarSources[node.avatarId];
										const avatarClipId = `${avatarClipPrefix}-avatar-${node.avatarId}`;
										return (
											<g
												key={node.id}
												data-testid={`${testId}-person-${node.avatarId}`}
												css={[styles.node, offset && styles.nodeDrift]}
												style={
													{
														'--node-delay': `${nodeDelay.get(node.id) ?? 0}s`,
														'--node-dx': `${offset?.dx ?? 0}px`,
														'--node-dy': `${offset?.dy ?? 0}px`,
													} as AnimationVariables
												}
											>
												{node.id === 'you' && !hasYouPulseFinished && (
													<circle
														css={styles.youPulse}
														cx={node.x}
														cy={node.y}
														r={node.radius + 7}
														fill="none"
														stroke={node.color}
														strokeWidth={1.5}
														onAnimationEnd={() => setHasYouPulseFinished(true)}
													/>
												)}
												<circle cx={node.x} cy={node.y} r={node.radius + 2} fill={node.color} />
												{avatarSource ? (
													<g>
														<clipPath id={avatarClipId}>
															<circle cx={node.x} cy={node.y} r={node.radius} />
														</clipPath>
														<image
															href={avatarSource}
															x={node.x - node.radius}
															y={node.y - node.radius}
															width={node.radius * 2}
															height={node.radius * 2}
															clipPath={`url(#${avatarClipId})`}
															preserveAspectRatio="xMidYMid slice"
														/>
													</g>
												) : (
													<g>
														<circle
															cx={node.x}
															cy={node.y}
															r={node.radius}
															fill={token('color.background.neutral.bold')}
														/>
														<text
															x={node.x}
															y={node.y}
															textAnchor="middle"
															dominantBaseline="central"
															fill={token('color.text.inverse')}
															fontSize={Math.max(10, node.radius * 0.7)}
															fontWeight={600}
														>
															{initials}
														</text>
													</g>
												)}
												{node.id === 'you' && (
													<text
														css={styles.personLabel}
														x={node.x}
														y={node.y + node.radius + 20}
														textAnchor="middle"
													>
														{formatMessage(messages.you)}
													</text>
												)}
											</g>
										);
									}

									if (node.kind === 'app') {
										return (
											<g
												key={node.id}
												css={[styles.node, styles.nodeDrift]}
												style={
													{
														'--node-delay': `${nodeDelay.get(node.id) ?? 0}s`,
														'--node-dx': `${offset?.dx ?? 0}px`,
														'--node-dy': `${offset?.dy ?? 0}px`,
													} as AnimationVariables
												}
											>
												<foreignObject x={node.x - 12} y={node.y - 12} width={24} height={24}>
													<ThirdPartyGlyph
														app={node.app}
														testId={`${testId}-third-party-${node.app}`}
													/>
												</foreignObject>
											</g>
										);
									}

									const label = getNodeLabel(node, formatMessage);
									const width = chipWidth(
										label,
										chipFont,
										newLozengeWidth,
										node.kind === 'product' && node.isNew,
									);
									const isPulsing =
										node.kind === 'product' && Boolean(node.isNew) && stage === node.stage;
									return (
										<g key={node.id}>
											{isPulsing && (
												<rect
													css={styles.pulse}
													x={node.x - width / 2 - 4}
													y={node.y - CHIP_HEIGHT / 2 - 4}
													width={width + 8}
													height={CHIP_HEIGHT + 8}
													rx={10}
													fill="none"
													stroke={token('color.border.brand')}
													strokeWidth={1.5}
												/>
											)}
											<foreignObject
												css={[
													styles.node,
													offset && node.stage === appsStage && styles.nodeDriftSoft,
													offset && node.stage !== appsStage && styles.nodeDrift,
												]}
												x={node.x - width / 2}
												y={node.y - CHIP_HEIGHT / 2}
												width={width}
												height={CHIP_HEIGHT}
												style={
													{
														'--node-delay': `${nodeDelay.get(node.id) ?? 0}s`,
														'--node-dx': `${offset?.dx ?? 0}px`,
														'--node-dy': `${offset?.dy ?? 0}px`,
													} as AnimationVariables
												}
											>
												<div css={[styles.chip, node.kind === 'work' && styles.chipNonTc]}>
													<span css={styles.glyph}>
														{node.kind === 'product' ? (
															<ProductGlyph product={node.product} />
														) : (
															<WorkIcon glyph={node.glyph} />
														)}
													</span>
													<span>{label}</span>
													{node.kind === 'product' && node.isNew && (
														<Lozenge appearance="discovery">{formatMessage(messages.new)}</Lozenge>
													)}
												</div>
											</foreignObject>
										</g>
									);
								})}

								{normalizedNewProducts.map((product) => {
									const node = nodeById.get(product);
									if (!node || stage < node.stage) {
										return null;
									}
									const callout = productCallouts[product];
									return (
										<foreignObject
											key={`callout-${product}`}
											x={node.x - callout.width / 2}
											y={node.y + CHIP_HEIGHT / 2 + 12}
											width={callout.width}
											height={92}
										>
											<div css={styles.callout}>{formatMessage(callout.message)}</div>
										</foreignObject>
									);
								})}
							</g>
						)}
					</svg>
					{canStart && (
						<VisuallyHidden aria-live="polite">{formatMessage(statusMessage)}</VisuallyHidden>
					)}
				</div>
			</div>
		</section>
	);
};

export const TwcgOnboardingIntroAnimation = (
	props: TwcgOnboardingIntroAnimationProps,
): JSX.Element => {
	return (
		<IntlMessagesProvider defaultMessages={defaultMessages} loaderFn={fetchMessagesForLocale}>
			<IntroAnimation {...props} />
		</IntlMessagesProvider>
	);
};
